import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckOutlined, DownloadOutlined } from "@ant-design/icons";
import { App, Button, Input, Typography, theme } from "antd";
import { observer } from "mobx-react";
import { useCallback, useState } from "react";
import { copyToClipboard } from "@devowl-wp/react-utils";
import { trackAnalyticsEvent } from "../../../../others/tracking.js";
import { __, _i } from "../../../../utils/i18n.js";
import { MATOMO_MASK } from "../../../../utils/matomoMask.js";
const externalLinkProps = {
    target: "_blank",
    rel: "noopener noreferrer"
};
/**
 * Boxed 3-step "get started" guide; shell mirrors the checkout card so the
 * post-sale view reads as a direct continuation. Product-name injection
 * uses Gettext `%s` (not `{{productName}}`) so it can coexist with `_i`'s
 * `{{tag}}` interpolation without stripping the opening tag.
 */ const LicenseSteps = observer(({ license, productName, websiteCount, customerCenterUrl, onLicenseCopied })=>{
    const { token } = theme.useToken();
    const { message } = App.useApp();
    const { licenseKey, productVariant: { latestVersionDownloadUrl } } = license;
    const [downloaded, setDownloaded] = useState(false);
    const handleDownload = useCallback(()=>{
        trackAnalyticsEvent("download_product", {
            ga: {
                event_category: "ecommerce",
                event_label: "Download product",
                value: productName
            },
            matomo: {
                category: "ecommerce",
                name: "Download product",
                value: productName
            }
        });
        window.open(latestVersionDownloadUrl, "_blank");
        setDownloaded(true);
    }, [
        latestVersionDownloadUrl,
        productName
    ]);
    const handleCopy = useCallback(()=>{
        copyToClipboard(licenseKey);
        message.success(__("License key copied to clipboard."));
        // Idempotent on the parent side — safe to fire on every copy.
        onLicenseCopied?.();
    }, [
        licenseKey,
        message,
        onLicenseCopied
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "checkout-v2-success__steps-card",
        style: {
            background: token.colorBgContainer
        },
        children: [
            /*#__PURE__*/ _jsx(Typography.Title, {
                level: 5,
                className: "checkout-v2-section-heading checkout-v2-success__section-title",
                style: {
                    margin: 0
                },
                children: __("How to get started with %s", productName)
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "checkout-v2-success__step",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-success__step-number",
                        style: {
                            background: token.colorPrimaryBg,
                            color: token.colorPrimary
                        },
                        children: "1"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-success__step-title",
                        children: __("Download %s", productName)
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-success__step-body",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "checkout-v2-success__step-text",
                            style: {
                                color: token.colorTextSecondary
                            },
                            children: __("You'll get a ZIP file containing the latest version of %s.", productName)
                        })
                    }),
                    latestVersionDownloadUrl && /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-success__step-action",
                        children: /*#__PURE__*/ _jsx(Button, {
                            type: "primary",
                            size: "large",
                            onClick: handleDownload,
                            icon: downloaded ? /*#__PURE__*/ _jsx(CheckOutlined, {}) : /*#__PURE__*/ _jsx(DownloadOutlined, {}),
                            children: downloaded ? __("Downloaded") : __("Download")
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "checkout-v2-success__step",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-success__step-number",
                        style: {
                            background: token.colorPrimaryBg,
                            color: token.colorPrimary
                        },
                        children: "2"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-success__step-title",
                        children: __("Install %s", productName)
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-success__step-body",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "checkout-v2-success__step-text",
                            style: {
                                color: token.colorTextSecondary
                            },
                            children: [
                                _i(__("Our {{a}}installation guide{{/a}} walks you through installing %s in WordPress step by step.", productName), {
                                    a: /*#__PURE__*/ _jsx("a", {
                                        href: __("https://devowl.io/knowledge-base/devowl-install-wordpress-plugin/"),
                                        ...externalLinkProps
                                    })
                                }),
                                " ",
                                /*#__PURE__*/ _jsx("em", {
                                    children: __("If you are already using the free version, simply install the newly purchased version alongside it.")
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "checkout-v2-success__step checkout-v2-success__step--license",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-success__step-number",
                        style: {
                            background: token.colorPrimaryBg,
                            color: token.colorPrimary
                        },
                        children: "3"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-success__step-title",
                        children: __("Activate your license key")
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "checkout-v2-success__step-body",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "checkout-v2-success__step-text",
                                style: {
                                    color: token.colorTextSecondary
                                },
                                children: __("Copy the key below and paste it into the %s plugin in WordPress to activate it.", productName)
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "checkout-v2-success__license-input",
                                ...MATOMO_MASK,
                                children: [
                                    /*#__PURE__*/ _jsx(Input, {
                                        readOnly: true,
                                        value: licenseKey,
                                        // Click (or Enter/Space with focus) copies, matching the Copy button affordance.
                                        onClick: handleCopy,
                                        onKeyDown: (event)=>{
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                handleCopy();
                                            }
                                        },
                                        "aria-label": __("License key - press Enter to copy"),
                                        style: {
                                            cursor: "pointer"
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx(Button, {
                                        type: "primary",
                                        size: "large",
                                        onClick: handleCopy,
                                        className: "checkout-v2-success__license-copy-button",
                                        children: __("Copy")
                                    })
                                ]
                            }),
                            websiteCount > 1 && // Anchored directly under the license input — proximity
                            // matters because experience shows buyers miss this
                            // notice otherwise and then expect the key to cover
                            // all purchased websites. Left-rule accent instead of
                            // a filled block so it reads as a "please note" aside
                            // without competing with the key for attention.
                            /*#__PURE__*/ _jsx("div", {
                                className: "checkout-v2-success__multi-website-notice",
                                style: {
                                    borderLeftColor: token.colorPrimary,
                                    background: token.colorInfoBg,
                                    color: token.colorTextHeading
                                },
                                children: _i(__("{{strong}}This license key is valid for {{u}}one{{/u}} website plus development environment.{{/strong}} Since you purchased multiple licenses, you can create and manage additional license keys for your websites in the {{a}}customer center{{/a}}."), {
                                    strong: /*#__PURE__*/ _jsx("strong", {}),
                                    u: /*#__PURE__*/ _jsx("u", {}),
                                    a: customerCenterUrl ? /*#__PURE__*/ _jsx("a", {
                                        href: customerCenterUrl,
                                        ...externalLinkProps
                                    }) : /*#__PURE__*/ _jsx("span", {})
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
export { LicenseSteps };
